/**
 * The public Discord bot's server approvals (public bot Phase B — owner's decision 2026-11-01: "Closed at first with
 * exceptions just like how we do access levels for the extension"), and the bot's own server terms that a server's admin
 * agrees to at the end of `/omni setup`.
 *
 * Approvals live in one small JSON file on the server (data/bot-servers.json, `BotServersFile`), written by the website's
 * Owner tab (apps/api routes/bot-servers.ts) and read by the bot (apps/discord core/approvals.ts) — the same way the access
 * table is. Like the extension's "Omni-Torn itself" row: `open` is the "every server" switch (off = closed), and each
 * server can be an exception either way (approved while closed, blocked while open). Servers the bot was already in
 * before approvals existed (only the owner could add it then: Public Bot was off) count as approved unless blocked.
 * The full Owner tiers table comes later; this is the minimal list.
 */

export type ServerDecision = 'approved' | 'blocked';

export interface ServerException {
  decision: ServerDecision;
  /** The owner's own note (who it is, why). */
  note: string;
  at: number;
}

export interface BotServers {
  /** Every server may use the bot's parts (the "every server" switch); exceptions still block. */
  open: boolean;
  /** Discord server id → the owner's exception for it. A blocked server keeps the bot, its parts off with one note — the bot never leaves by itself (owner, 2026-11-02). */
  servers: Record<string, ServerException>;
  /** Discord user id → blocked by the owner (G1): the bot answers them nothing but `/account`; servers they own have the parts off. */
  users: Record<string, BlockedUser>;
  /** The owner's global caps (Owner tab, F3). */
  caps: BotCaps;
  updatedAt: number | null;
}

export interface BlockedUser {
  note: string;
  at: number;
}

/**
 * Global caps the owner sets on the website's Owner tab (public bot F3/G1). Each has a default and a range; a value out of
 * range is clamped.
 */
export interface BotCaps {
  /** Giveaways running at once in one server (G1). */
  giveawaysPerServer: number;
  /** Stakeouts in one server. */
  stakeoutsPerServer: number;
  /** Background look-ups (auto-link, name refresh) an hour per server paid by lent keys / the pool (Phase D). */
  lookupsPerServerHour: number;
  /** Servers whose parts run at most; past it, a new server gets a polite note and its parts stay off — the bot never leaves (owner, 2026-11-02). */
  maxServers: number;
}

export const DEFAULT_BOT_CAPS: BotCaps = { giveawaysPerServer: 25, stakeoutsPerServer: 10, lookupsPerServerHour: 30, maxServers: 95 };
export const BOT_CAP_LIMITS: Record<keyof BotCaps, { min: number; max: number; label: string }> = {
  giveawaysPerServer: { min: 1, max: 100, label: 'Giveaways running per server' },
  stakeoutsPerServer: { min: 1, max: 25, label: 'Stakeouts per server' },
  lookupsPerServerHour: { min: 5, max: 120, label: 'Background look-ups an hour per server (lent keys)' },
  maxServers: { min: 1, max: 2400, label: 'Servers the bot joins at most' },
};

/** Only known caps, whole numbers within their ranges; anything missing takes its default. */
export function cleanCaps(raw: unknown): BotCaps {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out = { ...DEFAULT_BOT_CAPS };
  for (const k of Object.keys(BOT_CAP_LIMITS) as Array<keyof BotCaps>) {
    const v = r[k];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const { min, max } = BOT_CAP_LIMITS[k];
    out[k] = Math.max(min, Math.min(max, Math.round(v)));
  }
  return out;
}

export const NO_BOT_SERVERS: BotServers = { open: false, servers: {}, users: {}, caps: DEFAULT_BOT_CAPS, updatedAt: null };

/** A Discord id (snowflake): 15–21 digits — what the owner may type. */
export const isSnowflake = (x: unknown): x is string => typeof x === 'string' && /^\d{15,21}$/.test(x);
/** A server id as kept (tests use short names). */
const isGuildId = (x: unknown): x is string => typeof x === 'string' && /^[\w-]{1,32}$/.test(x);

export const MAX_SERVER_EXCEPTIONS = 2000;

export function cleanBotServers(raw: unknown): BotServers {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const servers: Record<string, ServerException> = {};
  const list = r.servers && typeof r.servers === 'object' && !Array.isArray(r.servers) ? Object.entries(r.servers as Record<string, unknown>) : [];
  for (const [id, v] of list.slice(0, MAX_SERVER_EXCEPTIONS)) {
    const e = v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
    if (!isGuildId(id) || !e || (e.decision !== 'approved' && e.decision !== 'blocked')) continue;
    servers[id] = { decision: e.decision, note: typeof e.note === 'string' ? e.note.slice(0, 200) : '', at: typeof e.at === 'number' && Number.isFinite(e.at) ? e.at : 0 };
  }
  const users: Record<string, BlockedUser> = {};
  const ulist = r.users && typeof r.users === 'object' && !Array.isArray(r.users) ? Object.entries(r.users as Record<string, unknown>) : [];
  for (const [id, v] of ulist.slice(0, MAX_SERVER_EXCEPTIONS)) {
    const e = v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
    if (!isGuildId(id) || !e) continue;
    users[id] = { note: typeof e.note === 'string' ? e.note.slice(0, 200) : '', at: typeof e.at === 'number' && Number.isFinite(e.at) ? e.at : 0 };
  }
  return { open: r.open === true, servers, users, caps: cleanCaps(r.caps), updatedAt: typeof r.updatedAt === 'number' ? r.updatedAt : null };
}

/** Why a server may (or may not) use the bot's parts. */
export type ApprovalState = 'approved' | 'open' | 'legacy' | 'blocked' | 'waiting';

/**
 * Where a server stands: blocked (an exception) > approved (an exception) > every server open > there before approvals >
 * waiting for the owner.
 */
export function serverApproval(file: BotServers, guildId: string, legacy: boolean): ApprovalState {
  const e = file.servers[guildId];
  if (e?.decision === 'blocked') return 'blocked';
  if (e?.decision === 'approved') return 'approved';
  if (file.open) return 'open';
  if (legacy) return 'legacy';
  return 'waiting';
}

export const approvalAllows = (s: ApprovalState): boolean => s !== 'blocked' && s !== 'waiting';

export const APPROVAL_LABEL: Record<ApprovalState, string> = {
  approved: 'Approved',
  open: 'Open (every server)',
  legacy: 'Approved (there before approvals)',
  blocked: 'Blocked',
  waiting: 'Waiting for approval',
};

/** What a server is for (setup step 1). */
export type ServerKind = 'faction' | 'factions' | 'community';
export const SERVER_KINDS: Record<ServerKind, { name: string; what: string }> = {
  faction: { name: 'Faction server', what: 'One Torn faction’s own server: alerts, banking and the faction’s member lists, tied to that faction.' },
  factions: { name: 'Several factions', what: 'An alliance or a family of factions: up to 5, each tied by its own leaders. Faction commands pick each member’s own faction; everything else works for everyone.' },
  community: { name: 'Community', what: 'No faction: linking, verification roles, giveaways and stakeouts.' },
};

// ---------------------------------------------------------------- the bot's server terms

/**
 * The bot's own terms for a Discord server, agreed by its admin at the end of `/omni setup` (recorded as
 * `core.termsAgreed = { version, by, tornId, at }` in the bot). Separate from Torn's terms table (key-terms.ts,
 * TERMS_VERSION), which every member still agrees to where they give a key. DRAFT for the coordinator/owner: a later
 * version may move into key-terms.ts or the website's /bot-terms page; a change of what's kept or posted bumps
 * BOT_SERVER_TERMS_VERSION and setup asks again.
 * Version 4 (public bot Phase C, 2026-11-02): up to 5 factions per server, tied only by a faction's leader, co-leader or a
 * member a leader named; the faction's leaders see (and can remove) every server that gets its data, with the server's
 * name and who tied it; a faction's other servers are told when a new one is tied; the faction's home server.
 * Version 5 (Omni-Torn's terms 19, "No age limits", 2026-11-02): the bot's counts and the control panel's server lists are
 * kept with no end date (before: 31 and 30 days).
 */
export const BOT_SERVER_TERMS_VERSION = 5;

export interface ServerTermsRow {
  id: string;
  name: string;
  text: string;
}

export const BOT_SERVER_TERMS: readonly ServerTermsRow[] = [
  {
    id: 'server-kept',
    name: 'What Omni-Torn keeps about this server',
    text: 'Its Discord id and name, the bot settings chosen here (server kind, the factions tied — up to 5 —, channels, roles, parts, command limits, stakeouts, each faction’s alerts) and what its parts record here (giveaways, bank requests, link and nickname changes). Who set it up and agreed to these terms (their Discord id and Torn player) is kept with it and shown to Omni-Torn’s owner, who approves servers; who tied each faction and when (their Torn name and id — never a key) is shown to this server’s admins in the control panel and to that faction’s leaders on Omni-Torn’s website, with this server’s name; his approval and his note about the server are kept until he changes them.',
  },
  {
    id: 'server-how-long',
    name: 'How long',
    text: 'Counts of what the bot did here (Torn calls by who paid, alerts posted, problems — numbers only) are kept with no end date (before version 5: 31 days). The servers where someone opened the control panel or used the bot as an Administrator are kept, to list their servers, until that person deletes their data (before version 5: 30 days). The settings are kept while the bot is in this server and for 30 days after it is removed (added back within 30 days, everything is as it was), then deleted. What the parts recorded (giveaways, bank requests) stays, like Omni-Torn’s other observations.',
  },
  {
    id: 'server-members',
    name: 'Members',
    text: 'Each member’s Discord id is linked to their Torn account only through Torn’s own Discord link or their own key. Their keys and data follow Omni-Torn’s terms (Torn’s table, at /terms), which they agree to where they give a key.',
  },
  {
    id: 'server-faction-data',
    name: 'Faction data here',
    text: 'Faction alerts, balances and member lists are posted only for a faction tied to this server by its leader or co-leader, or by a member they allowed on Omni-Torn’s website (Faction → Delegates) — a key with the faction’s API access alone doesn’t tie a server. Each faction’s data is read only with its own members’ keys and posted only in servers tied to it; in a server of several factions, faction commands answer with the member’s own faction. The faction’s leaders see every server that gets its data and can remove this one; its other servers are told (in their alerts channel) when a new server is tied, with this server’s name. One of its servers is its home server, where giveaways started in Torn post (the first tied, unless its leaders pick another). Everyone who can read the channels you pick sees what the bot posts there.',
  },
  {
    id: 'server-rules',
    name: 'Rules for this server',
    text: 'Follow Torn’s rules and Discord’s. No giveaways for real money, and don’t present your server as Omni-Torn. Omni-Torn may switch the bot’s parts off, leave or block a server. No warranty: alerts can be late or missing.',
  },
  {
    id: 'server-leaves',
    name: 'When the bot stops working here',
    text: 'The bot never leaves a server by itself. Its parts switch off here, with one note in the log channel (else the system channel, else a message to the server’s owner), when Omni-Torn blocks this server or a Discord user who owns it, when the bot is full (it is in as many servers as Omni-Torn allows for now), or while setup isn’t finished 30 days after it was added. Linking and /account keep working. Only Omni-Torn’s owner can remove it by hand, after a note. This server’s admins can remove it any time; its settings are kept for 30 days after it is removed.',
  },
  {
    id: 'server-delete',
    name: 'Deleting',
    text: 'Members delete their own data with /account delete (their bank requests go with it). When the admin who agreed deletes their data, the record keeps only that these terms were agreed, which version and when — not who. This server’s admins can export its data or delete it now in the control panel’s Data tab (settings, stakeouts and counts go; giveaways and bank requests stay, like other observations).',
  },
];

/** The record kept when a server's admin agrees (core.termsAgreed). */
export interface ServerTermsAgreed {
  version: number;
  /** Discord id of the admin who agreed (null once they deleted their data). */
  by: string | null;
  /** Their Torn player (from their key; null once they deleted their data). */
  tornId: number | null;
  at: number;
}

/** A Discord invite with these permissions (scopes bot + applications.commands, guild install). */
export const botInviteUrl = (appId: string, permissions: bigint | string): string =>
  `https://discord.com/oauth2/authorize?client_id=${encodeURIComponent(appId)}&scope=bot%20applications.commands&permissions=${String(permissions)}`;
